import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  serverTimestamp,
  query,
  where,
  writeBatch
} from 'firebase/firestore';

// User roles enum
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  OPERADOR = 'operador',
  VIEWER = 'viewer'
}

// User permissions interface
export interface UserPermissions {
  canCreateReports: boolean;
  canEditReports: boolean;
  canDeleteReports: boolean;
  canManageMaterials: boolean;
  canManageEquipment: boolean;
  canManageUsers: boolean;
  canViewAuditLogs: boolean;
  canExportData: boolean;
}

// Get permissions based on role
export const getRolePermissions = (role: UserRole): UserPermissions => {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return {
        canCreateReports: true,
        canEditReports: true,
        canDeleteReports: true,
        canManageMaterials: true,
        canManageEquipment: true,
        canManageUsers: true,
        canViewAuditLogs: true,
        canExportData: true,
      };
    case UserRole.ADMIN:
      return {
        canCreateReports: true,
        canEditReports: true,
        canDeleteReports: true,
        canManageMaterials: true,
        canManageEquipment: true,
        canManageUsers: false,
        canViewAuditLogs: true,
        canExportData: true,
      };
    case UserRole.OPERADOR:
      return {
        canCreateReports: true,
        canEditReports: true,
        canDeleteReports: false,
        canManageMaterials: true,
        canManageEquipment: false,
        canManageUsers: false,
        canViewAuditLogs: false,
        canExportData: true,
      };
    case UserRole.VIEWER:
      return {
        canCreateReports: false,
        canEditReports: false,
        canDeleteReports: false,
        canManageMaterials: false,
        canManageEquipment: false,
        canManageUsers: false,
        canViewAuditLogs: false,
        canExportData: true,
      };
    default:
      return getRolePermissions(UserRole.VIEWER);
  }
};

// Enhanced user interface for admin management
export interface AdminUser {
  id: string;
  email: string;
  isAdmin?: boolean; // Required for Firestore security rules
  role: UserRole;
  displayName?: string;
  createdAt: Date;
  updatedAt: Date;
  lastLogin?: Date;
  isActive: boolean;
  permissions: UserPermissions;
  createdBy?: string; // ID of admin who created this user
}

// Audit log interface
export interface AuditLogEntry {
  id: string;
  userId: string;
  userEmail: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'EXPORT' | 'ROLE_CHANGE';
  collection: string;
  documentId: string;
  changes?: {
    before: any;
    after: any;
    fieldsChanged: string[];
  };
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
}

// Database Migration System
export class DatabaseMigration {
  private migrationStatus: { [key: string]: boolean } = {};

  /**
   * Run all database migrations
   */
  async runMigrations(): Promise<void> {
    console.log('🔄 Starting database migrations...');

    try {
      await this.migrateUserManagement();
      await this.migrateExistingCollections();
      await this.initializeAuditSystem();
      await this.addMissingTimestamps();

      console.log('✅ All migrations completed successfully');
    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  }

  /**
   * Initialize user management system
   */
  private async migrateUserManagement(): Promise<void> {
    console.log('🔄 Migrating user management...');

    // Check if users collection exists
    const usersSnapshot = await getDocs(collection(db, 'usuariosAutorizados'));

    if (usersSnapshot.empty) {
      // Create initial super admin user
      const superAdminUser: AdminUser = {
        id: 'masterfem@gmail.com',
        email: 'masterfem@gmail.com',
        isAdmin: true,
        role: UserRole.SUPER_ADMIN,
        displayName: 'Master Admin',
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        permissions: getRolePermissions(UserRole.SUPER_ADMIN)
      };

      await setDoc(doc(db, 'usuariosAutorizados', 'masterfem@gmail.com'), superAdminUser);
      console.log('✅ Created super admin user');
    } else {
      // Update existing users with new fields if missing
      const BATCH_LIMIT = 499;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const updates: any = {};

        if (!userData.role) {
          updates.role = userData.email === 'masterfem@gmail.com' ? UserRole.SUPER_ADMIN : UserRole.VIEWER;
        }
        if (!userData.permissions) {
          const role = userData.role || UserRole.VIEWER;
          updates.permissions = getRolePermissions(role);
        }
        if (!userData.createdAt) {
          updates.createdAt = serverTimestamp();
        }
        if (!userData.updatedAt) {
          updates.updatedAt = serverTimestamp();
        }
        if (userData.isActive === undefined) {
          updates.isActive = true;
        }

        if (Object.keys(updates).length > 0) {
          batch.update(userDoc.ref, updates);
          batchCount++;
          if (batchCount >= BATCH_LIMIT) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }
      console.log('✅ Updated existing users with new fields');
    }
  }

  /**
   * Add missing fields to existing collections
   */
  private async migrateExistingCollections(): Promise<void> {
    console.log('🔄 Migrating existing collections...');

    const collections = [
      'operadoras',
      'equipos',
      'piletasTipos',
      'piletas',
      'pads',
      'pozos',
      'tramos'
    ];

    for (const collectionName of collections) {
      await this.addTimestampsToCollection(collectionName);
    }
  }

  /**
   * Add timestamps to documents that don't have them.
   * Chunks into batches of 499 to stay under Firestore's 500-operation limit.
   */
  private async addTimestampsToCollection(collectionName: string): Promise<void> {
    const snapshot = await getDocs(collection(db, collectionName));
    const BATCH_LIMIT = 499;
    let batch = writeBatch(db);
    let updatedCount = 0;
    let batchCount = 0;

    for (const document of snapshot.docs) {
      const data = document.data();
      const updates: any = {};

      if (!data.createdAt) {
        updates.createdAt = serverTimestamp();
      }
      if (!data.updatedAt) {
        updates.updatedAt = serverTimestamp();
      }

      // Add specific field migrations based on collection
      switch (collectionName) {
        case 'operadoras':
          if (data.habilitada === undefined) {
            updates.habilitada = true;
          }
          break;
        case 'equipos':
          if (data.habilitado === undefined) {
            updates.habilitado = true;
          }
          break;
        case 'piletas':
          if (data.habilitada === undefined) {
            updates.habilitada = true;
          }
          break;
      }

      if (Object.keys(updates).length > 0) {
        batch.update(document.ref, updates);
        updatedCount++;
        batchCount++;

        if (batchCount >= BATCH_LIMIT) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    if (updatedCount > 0) {
      console.log(`✅ Updated ${updatedCount} documents in ${collectionName}`);
    }
  }

  /**
   * Initialize audit log system
   */
  private async initializeAuditSystem(): Promise<void> {
    console.log('🔄 Initializing audit system...');

    // Create initial audit log entry
    const initialEntry: AuditLogEntry = {
      id: `migration_${Date.now()}`,
      userId: 'system',
      userEmail: 'system@migration',
      action: 'CREATE',
      collection: 'system',
      documentId: 'migration',
      timestamp: new Date(),
      changes: {
        before: {},
        after: { event: 'Database migration completed' },
        fieldsChanged: ['system_status']
      }
    };

    await setDoc(doc(db, 'auditLogs', initialEntry.id), initialEntry);
    console.log('✅ Audit system initialized');
  }

  /**
   * Add missing timestamps in batch
   */
  private async addMissingTimestamps(): Promise<void> {
    const collections = [
      'materiales',
      'serviciosTipo',
      'lookupTables',
      'equipmentBrands',
      'solidsDischargeTypes',
      'screenTypes',
      'mudTypes',
      'fluidProperties'
    ];

    for (const collectionName of collections) {
      try {
        const snapshot = await getDocs(collection(db, collectionName));
        if (!snapshot.empty) {
          await this.addTimestampsToCollection(collectionName);
        }
      } catch (error) {
        console.log(`ℹ️ Collection ${collectionName} doesn't exist yet, skipping...`);
      }
    }
  }

  /**
   * Check if migrations are needed
   */
  async checkMigrationsNeeded(): Promise<boolean> {
    try {
      // Check if usuariosAutorizados collection exists
      const usersSnapshot = await getDocs(query(
        collection(db, 'usuariosAutorizados'),
        where('email', '==', 'masterfem@gmail.com')
      ));

      return usersSnapshot.empty;
    } catch (error) {
      // If we can't check, assume migrations are needed
      return true;
    }
  }
}

// Audit logging utility
export class AuditLogger {
  static async logAction(
    userId: string,
    userEmail: string,
    action: AuditLogEntry['action'],
    collection: string,
    documentId: string,
    changes?: AuditLogEntry['changes']
  ): Promise<void> {
    try {
      const logEntry: AuditLogEntry = {
        id: `${action}_${collection}_${documentId}_${Date.now()}`,
        userId,
        userEmail,
        action,
        collection,
        documentId,
        changes,
        timestamp: new Date(),
        // In a real app, you'd capture these from the request
        ipAddress: undefined,
        userAgent: navigator.userAgent
      };

      await setDoc(doc(db, 'auditLogs', logEntry.id), logEntry);
    } catch (error) {
      console.error('Failed to log audit entry:', error);
      // Don't throw - logging shouldn't break the main operation
    }
  }

  static async getUserAuditHistory(userId: string, limit: number = 50): Promise<AuditLogEntry[]> {
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'auditLogs'),
          where('userId', '==', userId)
        )
      );

      return snapshot.docs
        .map(doc => ({ ...doc.data() } as AuditLogEntry))
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, limit);
    } catch (error) {
      console.error('Failed to fetch audit history:', error);
      return [];
    }
  }
}

// Migration runner - call this on app initialization
export const runDatabaseMigrations = async (): Promise<void> => {
  console.log('🔄 Starting database migrations...');

  try {
    // Test Firebase connection first
    console.log('🔄 Testing Firebase connection...');
    await getDocs(collection(db, 'operadoras'));
    console.log('✅ Firebase connection successful');

    const migration = new DatabaseMigration();

    if (await migration.checkMigrationsNeeded()) {
      console.log('🔄 Migrations needed, running...');
      await migration.runMigrations();
    } else {
      console.log('ℹ️ Database migrations up to date');
    }
  } catch (error) {
    console.error('❌ Database migration failed:', error);
    throw error; // Re-throw to handle in Dashboard
  }
};